import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'logistics',
    emoji: '🚚',
    title: '물류 관리자',
    category: '물류·유통',
    color: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    story: `물류 관리자 지훈씨는 하루 배송 가능 물량을 부등식으로 계산합니다.
트럭 한 대의 적재 한도가 2000 kg이고,
상자 한 개의 무게가 x kg일 때 실을 수 있는 상자 수를 구합니다.
"부등식으로 안전한 최대 적재량을 찾을 수 있어!"`,
    situation: '트럭 적재 한도 2000 kg, 상자 1개 무게 x kg. 상자 80개를 실으려면 x가 얼마 이하여야 하는가?',
    question: '80x ≤ 2000을 풀면 x의 범위는?',
    xLabel: '부등호',
    yLabel: '조건',
    equation: '80x ≤ 2000',
    answer: '80x ≤ 2000\nx ≤ 2000 ÷ 80\nx ≤ 25\n→ 상자 1개 무게가 25 kg 이하여야 한다.',
    whyStat: '물류 관리자는 일차부등식으로 적재 한도·비용 제한·납기 조건을 분석해 최적의 물류 계획을 세웁니다.',
    insight: 'CJ대한통운·쿠팡·롯데글로벌로지스 등 물류 기업의 관리자는 부등식으로 비용·용량·시간 제약을 분석해 배송 최적화를 합니다.',
    realAnswer: 25,
    unit: 'kg (이하)',
    tableData: [
      { x: 'x ≤ 5', y: '가능 (400 kg)' as unknown as number },
      { x: 'x ≤ 10', y: '가능 (800 kg)' as unknown as number },
      { x: 'x ≤ 25', y: '한계 (2000 kg)' as unknown as number },
      { x: 'x = 30', y: '초과 (2400 kg)' as unknown as number },
      { x: 'x = 50', y: '초과 (4000 kg)' as unknown as number },
    ],
    quiz: [
      { q: '부등식 3x + 5 ≤ 20을 풀면?', a: 'x ≤ 5', hint: '3x ≤ 20-5=15, x ≤ 15÷3=5' },
      { q: '부등식의 양변에 음수를 곱하거나 나눌 때 부등호 방향은?', a: '부등호 방향이 반대로 바뀐다 (≤ → ≥)', hint: '예: -2x ≤ 6 → x ≥ -3 (÷(-2)하면 방향 전환)' },
    ],
    jobs: ['물류 관리자', '공급망 분석가', '재고 최적화 전문가', '유통 MD'],
  },
  {
    id: 'trainer',
    emoji: '🏋️',
    title: '헬스 트레이너',
    category: '스포츠·건강',
    color: 'from-emerald-400 to-green-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `헬스 트레이너 수아씨는 회원의 하루 칼로리 목표를 부등식으로 관리합니다.
기초대사량 1400 kcal에 운동 소모 칼로리를 더해
하루 총 소모량이 1800 kcal 이상이 되도록 운동량을 설정합니다.
"부등식으로 최소 운동량을 정확히 계산할 수 있어!"`,
    situation: '기초대사량 1400 kcal, 1시간 운동 시 x kcal 소모. 하루 총 소모가 1800 kcal 이상이 되려면 운동 시간은?',
    question: '1400 + 60x ≥ 1800 (x: 운동 분 수)를 풀면?',
    xLabel: '부등호',
    yLabel: '조건',
    equation: '1400 + 60x ≥ 1800',
    answer: '60x ≥ 1800 - 1400\n60x ≥ 400\nx ≥ 400 ÷ 60\nx ≥ 6.67\n→ 하루 최소 약 7분 이상 운동(시간당 60 kcal 기준)',
    whyStat: '헬스 트레이너는 일차부등식으로 회원별 목표 칼로리·운동 강도·체중 변화 조건을 설계해 맞춤 운동 계획을 수립합니다.',
    insight: '스포츠 센터·병원 재활팀·스포츠 의학 전문가는 부등식으로 안전한 운동 범위를 정해 부상 없이 체력을 향상시킵니다.',
    realAnswer: 7,
    unit: '분 (이상)',
    tableData: [
      { x: 'x = 5분', y: '부족 (1700 kcal)' as unknown as number },
      { x: 'x = 7분', y: '거의 충족' as unknown as number },
      { x: 'x = 10분', y: '충족 (2000 kcal)' as unknown as number },
      { x: 'x = 30분', y: '충분 (3200 kcal)' as unknown as number },
      { x: 'x = 60분', y: '충분 (5000 kcal)' as unknown as number },
    ],
    quiz: [
      { q: '2x - 3 ≥ 7을 풀면?', a: 'x ≥ 5', hint: '2x ≥ 7+3=10, x ≥ 10÷2=5' },
      { q: '부등식의 해를 수직선에 나타낼 때 x ≥ 5는 어떻게 표시하나요?', a: '5에 닫힌 원(●)을 그리고 오른쪽 방향으로 화살표', hint: '≥는 5 포함(●), >는 5 미포함(○)' },
    ],
    jobs: ['헬스 트레이너', '스포츠 의학 전문가', '재활 치료사', '운동처방사'],
  },
  {
    id: 'restaurant',
    emoji: '🍽️',
    title: '레스토랑 오너',
    category: '요식업·경영',
    color: 'from-rose-400 to-pink-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    badge: 'bg-rose-100 text-rose-700',
    story: `레스토랑 오너 민준씨는 손익분기점을 부등식으로 계산합니다.
하루 고정 비용이 50만원이고 메뉴 1개 판매 시 이익이 5000원일 때
이익이 나려면 하루 최소 몇 개를 팔아야 하는지 구합니다.
"부등식으로 수익성을 분석해 경영 전략을 세워!"`,
    situation: '하루 고정 비용 500,000원, 메뉴 1개 이익 5,000원. 하루 순이익 > 0이 되려면 판매량 x는?',
    question: '5000x - 500000 > 0을 풀면 x의 범위는?',
    xLabel: '판매량 (x개)',
    yLabel: '순이익 (원)',
    equation: '5000x - 500000 > 0',
    answer: '5000x > 500000\nx > 500000 ÷ 5000\nx > 100\n→ 하루 100개 초과 판매해야 이익 발생',
    whyStat: '레스토랑 오너는 일차부등식으로 손익분기점·최소 판매량·최대 할인율을 계산해 수익성 있는 경영 계획을 수립합니다.',
    insight: '외식 프랜차이즈·카페·급식 기업의 경영자는 부등식으로 원가·판매가·판매량의 관계를 분석해 메뉴 가격과 마케팅 전략을 결정합니다.',
    realAnswer: 100,
    unit: '개 (초과)',
    tableData: [
      { x: 'x = 50', y: -250000 },
      { x: 'x = 80', y: -100000 },
      { x: 'x = 100', y: 0 },
      { x: 'x = 120', y: 100000 },
      { x: 'x = 150', y: 250000 },
    ],
    quiz: [
      { q: '-3x + 12 > 0을 풀면?', a: 'x < 4', hint: '-3x > -12, x < 4 (음수로 나누면 부등호 방향 바뀜)' },
      { q: '손익분기점(BEP)이란?', a: '수익과 비용이 같아지는 점, 즉 이익 = 0인 판매량', hint: '총수익 - 총비용 = 0이 되는 판매량' },
    ],
    jobs: ['레스토랑 오너', '프랜차이즈 경영자', '원가 분석가', '경영 컨설턴트'],
  },
  {
    id: 'cloud',
    emoji: '☁️',
    title: '클라우드 엔지니어',
    category: '과학·IT',
    color: 'from-sky-400 to-blue-500',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    badge: 'bg-sky-100 text-sky-700',
    story: `클라우드 엔지니어 예은씨는 서버 비용을 부등식으로 관리합니다.
기본 요금 월 10만원 + 데이터 1 GB당 500원.
월 예산 20만원 이내로 사용하려면 데이터를 얼마나 쓸 수 있는지 계산합니다.
"부등식으로 IT 비용 최적화를 설계해!"`,
    situation: '월 기본료 100,000원, 데이터 1 GB당 500원. 월 총 비용 ≤ 200,000원이 되려면 데이터 사용량 x는?',
    question: '100000 + 500x ≤ 200000을 풀면 x의 범위는?',
    xLabel: '데이터 사용량 (GB)',
    yLabel: '월 비용 (원)',
    equation: '100000 + 500x ≤ 200000',
    answer: '500x ≤ 200000 - 100000\n500x ≤ 100000\nx ≤ 100000 ÷ 500\nx ≤ 200\n→ 월 200 GB 이하 사용 가능',
    whyStat: '클라우드 엔지니어는 일차부등식으로 서버 비용·처리 속도·저장 용량 제약을 분석해 비용 효율적인 인프라를 설계합니다.',
    insight: 'AWS·Azure·GCP 등 클라우드 기업의 엔지니어는 부등식으로 비용 한도·성능 요구사항을 동시에 만족하는 최적 설정을 찾습니다.',
    realAnswer: 200,
    unit: 'GB (이하)',
    tableData: [
      { x: 'x = 50', y: 125000 },
      { x: 'x = 100', y: 150000 },
      { x: 'x = 150', y: 175000 },
      { x: 'x = 200', y: 200000 },
      { x: 'x = 250', y: 225000 },
    ],
    quiz: [
      { q: '5x + 3 ≤ 28을 풀면?', a: 'x ≤ 5', hint: '5x ≤ 28-3=25, x ≤ 25÷5=5' },
      { q: '부등식의 해가 x ≤ 200일 때 x = 200은 해에 포함되나요?', a: '예! ≤(이하)이므로 200 포함. <(미만)이면 미포함', hint: '≤, ≥: 등호 포함(닫힌 원●). <, >: 등호 미포함(열린 원○)' },
    ],
    jobs: ['클라우드 엔지니어', 'DevOps 엔지니어', '데이터 인프라 설계자', 'IT 비용 분석가'],
  },
  {
    id: 'investor',
    emoji: '📈',
    title: '투자 분석가',
    category: '금융·경제',
    color: 'from-violet-400 to-purple-500',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    badge: 'bg-violet-100 text-violet-700',
    story: `투자 분석가 준혁씨는 안전 투자 비율을 부등식으로 계산합니다.
총 투자금 1000만원 중 위험 자산 비율 x%,
손실 한도를 위험 자산 투자금의 10%로 제한할 때
최대 손실이 50만원을 넘지 않으려면 x를 구합니다.
"부등식으로 리스크 한도 내 최적 포트폴리오를 설계해!"`,
    situation: '투자금 1000만원, 위험 자산 비율 x%. 최대 손실 = 위험 자산 × 10% ≤ 50만원. x를 구하시오.',
    question: '(1000 × x/100) × 0.1 ≤ 50을 풀면 x의 범위는?',
    xLabel: '위험 자산 비율 x (%)',
    yLabel: '최대 손실 (만원)',
    equation: '10x/10 ≤ 50  →  x ≤ 50',
    answer: '(1000 × x/100) × 0.1 ≤ 50\n(10x) × 0.1 ≤ 50\nx ≤ 50\n→ 위험 자산 비율 50% 이하로 유지',
    whyStat: '투자 분석가는 일차부등식으로 포트폴리오 리스크 한도·수익률 조건·손실 한도를 분석해 안전한 투자 전략을 수립합니다.',
    insight: '증권사·자산운용사·핀테크 기업의 투자 분석가는 부등식으로 위험 허용 범위를 정해 고객별 맞춤 포트폴리오를 설계합니다.',
    realAnswer: 50,
    unit: '% (이하)',
    tableData: [
      { x: 'x = 20%', y: 20 },
      { x: 'x = 30%', y: 30 },
      { x: 'x = 50%', y: 50 },
      { x: 'x = 70%', y: 70 },
      { x: 'x = 100%', y: 100 },
    ],
    quiz: [
      { q: '부등식 -2x + 10 ≥ 4를 풀면?', a: 'x ≤ 3', hint: '-2x ≥ 4-10=-6, x ≤ -6÷(-2)=3 (음수 나누기로 부등호 전환)' },
      { q: '리스크 분산 투자에서 부등식이 필요한 이유는?', a: '손실 한도(최대값)와 수익 목표(최솟값)를 동시에 만족하는 범위를 구하기 위해', hint: '부등식 = "범위"를 구하는 도구' },
    ],
    jobs: ['투자 분석가', '펀드 매니저', '리스크 관리자', '핀테크 개발자'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '🚚',
    title: '물류·공급망 관리자',
    example: '적재량·배송 비용·납기 제약을 부등식으로 최적화',
    formula: 'ax ≤ b (적재량 제한)',
    salary: '연 4,000만~9,000만원',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: '🏋️',
    title: '스포츠·재활 전문가',
    example: '안전 운동 강도·칼로리 목표를 부등식으로 설계',
    formula: 'ax + b ≥ c (목표 칼로리)',
    salary: '연 3,500만~8,000만원',
    color: 'from-emerald-500 to-green-500',
  },
  {
    icon: '🍽️',
    title: '경영·원가 분석가',
    example: '손익분기점·최소 판매량을 부등식으로 계산',
    formula: 'px - c > 0 (이익 조건)',
    salary: '연 4,500만~1억원',
    color: 'from-rose-500 to-pink-500',
  },
  {
    icon: '☁️',
    title: 'IT·클라우드 엔지니어',
    example: '서버 비용·처리 용량 제약을 부등식으로 설계',
    formula: 'ax + b ≤ M (예산 한도)',
    salary: '연 5,000만~1억 2,000만원',
    color: 'from-sky-500 to-blue-500',
  },
  {
    icon: '📈',
    title: '금융·투자 분석가',
    example: '포트폴리오 리스크 한도·수익 목표를 부등식으로 관리',
    formula: 'rx ≤ L (손실 한도)',
    salary: '연 5,000만~1억 5,000만원',
    color: 'from-violet-500 to-purple-500',
  },
  {
    icon: '🏗️',
    title: '건설·안전 공학자',
    example: '구조물 하중 한도·안전 계수를 부등식으로 검증',
    formula: 'F ≤ σ × A (하중 한도)',
    salary: '연 4,500만~1억원',
    color: 'from-indigo-500 to-violet-500',
  },
]

// ── 도전 퀴즈 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '📋',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '일차부등식 4x - 3 < 9를 풀고, 해를 수직선에 나타내는 방법을 설명하시오.',
    hint: '4x < 9+3=12, x < 3. 수직선: 3에 열린 원(○) 그리고 왼쪽 화살표',
    answer: 'x < 3',
    explanation: '4x - 3 < 9\n4x < 9 + 3 = 12\nx < 12 ÷ 4 = 3\n수직선: 3 위치에 열린 원(○), 왼쪽으로 화살표\n(< 이므로 3은 포함 안 됨)',
  },
  {
    id: 'c2',
    emoji: '🌿',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '음수 계수 부등식을 풀어보자.\n-5x + 10 ≥ -15를 풀고 부등호 변화에 주의하시오.',
    hint: '-5x ≥ -15-10=-25, 양변을 -5로 나누면 부등호 방향 바뀜!',
    answer: 'x ≤ 5',
    explanation: '-5x + 10 ≥ -15\n-5x ≥ -15 - 10\n-5x ≥ -25\n양변을 -5로 나누면 (부등호 방향 전환!)\nx ≤ -25 ÷ (-5) = 5\n→ x ≤ 5',
  },
  {
    id: 'c3',
    emoji: '📈',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '활용 문제: 연필 1자루 300원, 공책 1권 1200원. 용돈 6000원으로 공책 3권을 사고 연필을 최대 몇 자루 살 수 있나?',
    hint: '공책 비용 = 3×1200 = 3600원. 남은 돈으로 연필 구입: 300x ≤ 6000-3600',
    answer: '최대 8자루',
    explanation: '공책 3권 비용: 3 × 1200 = 3600원\n남은 돈: 6000 - 3600 = 2400원\n연필 x자루: 300x ≤ 2400\nx ≤ 2400 ÷ 300 = 8\n→ 최대 8자루',
  },
  {
    id: 'c4',
    emoji: '🔬',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '두 부등식을 동시에 만족하는 x의 범위를 구하시오.\n2x - 1 > 3  AND  3x + 4 ≤ 19',
    hint: '각 부등식을 따로 풀고, 교집합(둘 다 만족하는 범위)을 구한다',
    answer: '2 < x ≤ 5',
    explanation: '① 2x - 1 > 3 → 2x > 4 → x > 2\n② 3x + 4 ≤ 19 → 3x ≤ 15 → x ≤ 5\n교집합: x > 2 AND x ≤ 5\n→ 2 < x ≤ 5\n수직선: 2에 열린 원(○), 5에 닫힌 원(●)',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  '물류·유통': 'bg-amber-100 text-amber-700',
  '스포츠·건강': 'bg-emerald-100 text-emerald-700',
  '요식업·경영': 'bg-rose-100 text-rose-700',
  '과학·IT': 'bg-sky-100 text-sky-700',
  '금융·경제': 'bg-violet-100 text-violet-700',
}

// ── 계산기 컴포넌트 ───────────────────────────────────────────────────────────
type CalcMode = 'leq' | 'geq'

interface CalcResult {
  mode: CalcMode
  steps: string[]
  outputVal: number | string
}

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [mode, setMode] = useState<CalcMode>('leq')
  const [coef, setCoef] = useState('')
  const [constant, setConstant] = useState('')
  const [bound, setBound] = useState('')
  const [result, setResult] = useState<CalcResult | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const reset = () => { setCoef(''); setConstant(''); setBound(''); setResult(null); setUserAnswer(''); setChecked(null) }
  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    const a = parseFloat(coef)
    const b = parseFloat(constant)
    const c = parseFloat(bound)
    if (isNaN(a) || isNaN(b) || isNaN(c) || a === 0) return

    // ax + b ≤ c  or  ax + b ≥ c
    const rhs = c - b
    const sol = rhs / a
    const flip = a < 0

    if (mode === 'leq') {
      const sign = flip ? '≥' : '≤'
      setResult({
        mode,
        outputVal: sol,
        steps: [
          `부등식: ${a}x + ${b} ≤ ${c}`,
          `${a}x ≤ ${c} - ${b} = ${rhs}`,
          `x ${a < 0 ? '≥ (음수 나누기, 방향 전환)' : '≤'} ${rhs} ÷ ${a}`,
          `x ${sign} ${sol}`,
        ],
      })
    } else {
      const sign = flip ? '≤' : '≥'
      setResult({
        mode,
        outputVal: sol,
        steps: [
          `부등식: ${a}x + ${b} ≥ ${c}`,
          `${a}x ≥ ${c} - ${b} = ${rhs}`,
          `x ${a < 0 ? '≤ (음수 나누기, 방향 전환)' : '≥'} ${rhs} ÷ ${a}`,
          `x ${sign} ${sol}`,
        ],
      })
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const outVal = typeof result.outputVal === 'number' ? result.outputVal : parseFloat(String(result.outputVal))
    const tolerance = Math.abs(outVal) * 0.02 + 0.001
    setChecked(Math.abs(ua - outVal) <= tolerance)
  }

  return (
    <div className="card border-2 border-violet-100">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-violet-600" />
          직접 계산해보기
        </p>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('leq')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'leq' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            ax+b ≤ c
          </button>
          <button
            onClick={() => handleModeChange('geq')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'geq' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            ax+b ≥ c
          </button>
        </div>
      </div>

      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">공식</span>
        <span className="font-mono font-bold text-violet-700 text-sm">
          {mode === 'leq' ? 'ax + b ≤ c  →  x ≤ (c-b)/a' : 'ax + b ≥ c  →  x ≥ (c-b)/a'}
        </span>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">🔢 계수 a</label>
          <input
            type="number"
            value={coef}
            onChange={e => { setCoef(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder="예: 80"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all"
          />
        </div>
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">🔢 상수 b</label>
          <input
            type="number"
            value={constant}
            onChange={e => { setConstant(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder="예: 0"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all"
          />
        </div>
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">🔢 우변 c</label>
          <input
            type="number"
            value={bound}
            onChange={e => { setBound(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder="예: 2000"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={!coef || !constant || !bound}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
              mode === 'leq'
                ? 'bg-violet-600 hover:bg-violet-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
            }`}
          >
            계산!
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3 flex items-center gap-1">
              📝 풀이 과정 (단계별)
            </p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1 ? 'bg-violet-500 text-white' : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1 ? 'font-black text-violet-700 text-base' : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'leq' ? 'bg-violet-50 border-violet-300' : 'bg-indigo-50 border-indigo-300'
          }`}>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-black ${mode === 'leq' ? 'text-violet-700' : 'text-indigo-700'}`}>
                x {mode === 'leq' ? (parseFloat(coef) < 0 ? '≥' : '≤') : (parseFloat(coef) < 0 ? '≤' : '≥')} {result.outputVal}
              </span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기 (x의 경계값 입력)</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder="경계값 숫자만 입력"
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉</>
                  : <><XCircle size={16} /> 풀이 과정을 다시 확인해보세요!</>}
              </div>
            )}
          </div>
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-violet-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 컴포넌트 ──────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{ch.emoji}</span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
        </div>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3 whitespace-pre-line">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-violet-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-violet-600 leading-relaxed whitespace-pre-line">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function A2_2RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers', label: '직업과 부등식', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 일차부등식</h1>
            <p className="text-sm text-slate-500">물류 관리자, 트레이너, 레스토랑 오너, 클라우드 엔지니어, 투자 분석가 — 모두 부등식을 활용합니다!</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 일차부등식을 만나다!</p>
          <p className="text-sm opacity-90 mb-3">부등식의 성질·풀이·수직선 표현이 세상 모든 한계·목표·제약 분석의 기초입니다.</p>
          <div className="grid grid-cols-4 md:grid-cols-5 gap-2">
            {[
              { icon: '🚚', job: '물류 관리자' },
              { icon: '🏋️', job: '헬스 트레이너' },
              { icon: '🍽️', job: '레스토랑 오너' },
              { icon: '☁️', job: '클라우드 엔지니어' },
              { icon: '📈', job: '투자 분석가' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-violet-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 ── */}
      {activeTab === 'scenarios' && (
        <>
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-violet-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 좌측: 시나리오 목록 */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category]}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-violet-600 font-semibold">{scenario.equation}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* 우측: 시나리오 상세 */}
            <div className="lg:col-span-2 space-y-4">
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category]}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 일차부등식</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.story}</p>
                </div>

                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 일차부등식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-base font-black text-violet-700 font-mono">{selected.equation}</span>
                  </div>
                </div>
              </div>

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-violet-700 whitespace-pre-line">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-blue-700">
                    <span className="font-semibold">왜 부등식인가?</span> {selected.whyStat}
                  </p>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={15} className="text-violet-500" /> 조건 분석표로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '표 접기' : '표 펼치기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>
                {showTable && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">{selected.xLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-violet-50 text-violet-700 text-left">{selected.yLabel}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-violet-700">{String(row.y)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!showTable && (
                  <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500 text-sm">
                    <p className="text-2xl mb-1">📋</p>
                    <p>표 펼치기를 눌러 조건 분석표를 확인해보세요!</p>
                  </div>
                )}
              </div>

              <ScenarioCalculator scenario={selected} />

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-violet-500" /> 이 상황에서 부등식을 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-violet-100 text-violet-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['부등식 성질', '일차부등식 풀기', '음수 곱셈/나눗셈', '수직선 표현', '범위 표현', '활용 문제'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 card bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 일차부등식 핵심 개념 정리</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '⚖️', title: '부등식의 성질', desc: '양변에 같은 수를 더하거나 빼도 부등호 방향 유지\n양변에 양수를 곱/나눠도 방향 유지\n양변에 음수를 곱/나누면 부등호 방향 전환!\na > b이면 a+c > b+c', color: 'text-violet-600' },
                { icon: '📐', title: '일차부등식 풀기', desc: '① 이항: 상수를 우변으로\n② 계수로 나누기 (음수면 방향 전환)\n③ 해: x > k, x ≥ k, x < k, x ≤ k\n④ 수직선으로 표현', color: 'text-indigo-600' },
                { icon: '📏', title: '수직선 표현법', desc: '≤, ≥: 닫힌 원(●) — 경계 포함\n<, >: 열린 원(○) — 경계 미포함\nx ≥ 3: 3에 ●, 오른쪽 화살표\nx < 5: 5에 ○, 왼쪽 화살표', color: 'text-purple-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed whitespace-pre-line">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업 탐색 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 일차부등식을 사용하는 직업들</h2>
            <p className="text-sm text-violet-100">
              부등식의 성질·일차부등식 풀기·수직선 표현은 물류·의료·경영·IT·금융 등 수많은 직업의 핵심 분석 도구입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-violet-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 부등식이 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: '최적화·선형 프로그래밍', desc: '물류 경로, 공장 생산 계획 등 복잡한 제약 조건을 부등식으로 표현하고 최적 해를 찾습니다.', color: 'border-yellow-400' },
                { title: '머신러닝 학습 조건', desc: '모델 학습 시 손실 함수가 특정 값 이하(≤)가 되도록 학습을 제어하는 조건이 부등식입니다.', color: 'border-violet-400' },
                { title: '금융 리스크 관리', desc: '포트폴리오 손실 한도(VaR)를 부등식으로 정의해 안전한 투자 범위를 결정합니다.', color: 'border-emerald-400' },
                { title: '공학적 안전 설계', desc: '구조물의 하중 한도, 전기 회로의 전류 한계를 부등식으로 표현해 안전한 설계를 검증합니다.', color: 'border-sky-400' },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card border border-violet-200 bg-violet-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-violet-600" /> 부등식에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중1', topic: '정수·유리수', active: false },
                { step: '중2', topic: '일차부등식', active: true },
                { step: '중3', topic: '이차방정식', active: false },
                { step: '고1', topic: '연립부등식', active: false },
                { step: '고2', topic: '이차부등식', active: false },
                { step: '대학', topic: '최적화 이론', active: false },
                { step: '직업', topic: '선형 프로그래밍', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-violet-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 6 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-violet-100">
              실제 상황에서 일차부등식을 직접 풀어보세요!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-violet-500" /> 일차부등식 핵심 공식 정리
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '① 부등식 성질', desc: '덧셈·뺄셈: 방향 유지\n양수 곱/나누기: 방향 유지\n음수 곱/나누기: 방향 전환!\na>b, c>0이면 ac>bc\na>b, c<0이면 ac<bc', example: '예: -2x>6 → x<-3', color: 'bg-violet-50 border-violet-200 text-violet-800' },
                { step: '② 풀이 순서', desc: '① 괄호 전개 (분배법칙)\n② 이항 (x를 좌변, 상수를 우변)\n③ 동류항 정리\n④ 계수로 나누기\n(음수면 부등호 전환!)', example: '예: 3x-1≤8 → x≤3', color: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
                { step: '③ 수직선 & 활용', desc: '≤, ≥: 닫힌 원(●)\n<, >: 열린 원(○)\n활용: 문제에서 부등식 세우기\n→ 최솟값/최댓값 구하기\n정수 해: 범위 내 정수 나열', example: '예: x<3 → 정수 해: …0,1,2', color: 'bg-purple-50 border-purple-200 text-purple-800' },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2 whitespace-pre-line">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-violet-50 border border-violet-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-violet-700">부등식 세우기 → 이항 → 계수 나누기 (음수 주의!) → 수직선 표현!</p>
              <p className="text-xs text-violet-500 mt-1">부등식의 흐름을 이해하면 제약 조건이 있는 모든 문제를 풀 수 있습니다!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
